import { workflowDefinitionsApi, type WorkflowDefinitionEntityContext } from '@/api/workflowDefinitions'
import type { CreateWorkflowDefinitionRequest, PaginationParams, UpdateWorkflowDefinitionRequest, WorkflowDefinition, WorkflowDefinitionFilters, WorkflowValidationResult } from '@/types'
import type { ClaudeRunOverrides } from '@/types/claude'
import type { CopilotRunOverrides } from '@/types/copilot'
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useTenantKey } from './useTenantKey'

const ALL_DEFINITIONS_PAGINATION: PaginationParams = { skipCount: 0, maxResultCount: 10000 }

const TRIGGER_SCHEMA_KEY_SEGMENT = 'workflow-trigger-schema'

export function invalidateWorkflowTriggerSchemas(queryClient: QueryClient) {
  queryClient.invalidateQueries({
    predicate: query => query.queryKey.includes(TRIGGER_SCHEMA_KEY_SEGMENT),
  })
}

export function useWorkflowDefinitions(projectId: string, pagination?: PaginationParams, filters?: WorkflowDefinitionFilters, entityContext?: WorkflowDefinitionEntityContext) {
  const tenantKey = useTenantKey()
  const pag = pagination ?? ALL_DEFINITIONS_PAGINATION
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'workflow-definitions', { ...filters, ...pag, ...entityContext }),
    queryFn: () => workflowDefinitionsApi.getAll(projectId, filters, pag, entityContext),
    enabled: !!projectId,
  })
}

export function useWorkflowDefinition(projectId: string, id: string) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'workflow-definitions', id),
    queryFn: () => workflowDefinitionsApi.getById(projectId, id),
    enabled: !!projectId && !!id,
  })
}

export function useCreateWorkflowDefinition(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: (data: CreateWorkflowDefinitionRequest) =>
      workflowDefinitionsApi.create(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'workflow-definitions') })
    },
  })
}

export function useUpdateWorkflowDefinition(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateWorkflowDefinitionRequest }) =>
      workflowDefinitionsApi.update(projectId, id, data),
    onSuccess: (result, { id, data }) => {
      queryClient.setQueryData<WorkflowDefinition>(
        tenantKey('projects', projectId, 'workflow-definitions', id),
        old => old ? { ...old, ...data, version: result.version } : old,
      )
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'workflow-definitions') })
    },
  })
}

export function useDuplicateWorkflowDefinition(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: (id: string) =>
      workflowDefinitionsApi.duplicate(projectId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'workflow-definitions') })
    },
  })
}

export function useToggleWorkflowDefinitionActive(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: (id: string) =>
      workflowDefinitionsApi.toggleActive(projectId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'workflow-definitions') })
    },
  })
}

export function useDeleteWorkflowDefinition(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: (id: string) =>
      workflowDefinitionsApi.delete(projectId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'workflow-definitions') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'workflow-runs') })
    },
  })
}

export function useValidateWorkflowYaml(projectId: string) {
  return useMutation<WorkflowValidationResult, Error, string>({
    mutationFn: (yamlContent: string) =>
      workflowDefinitionsApi.validateYaml(projectId, yamlContent),
  })
}

export function useTriggerWorkflowDefinition(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: ({ name, data }: { name: string; data: { inputValuesJson?: string; linkedEntityId?: string; linkedEntityType?: 'WorkItem' | 'Intent' | 'Project'; claudeOverrides?: ClaudeRunOverrides; copilotOverrides?: CopilotRunOverrides; sandboxImageId?: string | null; forceRerun?: boolean; agentProviderOverride?: string } }) =>
      workflowDefinitionsApi.trigger(projectId, name, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'workflow-runs') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'work-items') })
    },
  })
}

export function useWorkflowYamlSnapshots(projectId: string, definitionId: string | undefined) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'workflow-definitions', definitionId, 'yaml-snapshots'),
    queryFn: () => workflowDefinitionsApi.getYamlSnapshots(projectId, definitionId!),
    enabled: !!projectId && !!definitionId,
  })
}

export function useWorkflowTriggerSchema(projectId: string, name: string | null) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'workflow-trigger-schema', name),
    queryFn: () => workflowDefinitionsApi.getTriggerSchema(projectId, name!),
    enabled: !!projectId && !!name,
    retry: false,
  })
}
